import { Apple, ChefHat, Cookie, Croissant, Snowflake, Soup, type LucideIcon } from 'lucide-react';
import styled from 'styled-components';

import { ProductFlow } from 'data/__generated__';
import { BUNDLE_TYPES_LABELS } from 'pages/Bundles/constants/types';
import { colors } from 'constants/colors';
import { returnNever } from 'utils/typing';

const getColorsFromType = (type: ProductFlow | null) => {
    const { pantinGrey, green, orange, primaryYellow, pureWhite, black, emeraldBlue, red, blue } = colors;
    if (!type) return { backgroundColor: pureWhite, borderColor: pantinGrey, labelColor: black };

    switch (type) {
        case ProductFlow.Fresh:
            return { backgroundColor: emeraldBlue, borderColor: emeraldBlue, labelColor: pureWhite };
        case ProductFlow.Dry:
            return { backgroundColor: green, borderColor: green, labelColor: pureWhite };
        case ProductFlow.Bakery:
            return { backgroundColor: orange, borderColor: orange, labelColor: black };
        case ProductFlow.Fruit:
            return { backgroundColor: primaryYellow, borderColor: primaryYellow, labelColor: black };
        case ProductFlow.Caterer:
            return { backgroundColor: red, borderColor: red, labelColor: black };
        case ProductFlow.Kumo:
            return { backgroundColor: blue, borderColor: blue, labelColor: pureWhite };
        default:
            return returnNever(type);
    }
};

/** Le pendant iconographique de `BUNDLE_TYPES_LABELS` : un flux, un glyphe. */
const FLOW_ICONS: Record<ProductFlow, LucideIcon> = {
    [ProductFlow.Dry]: Cookie,
    [ProductFlow.Fresh]: Snowflake,
    [ProductFlow.Bakery]: Croissant,
    [ProductFlow.Fruit]: Apple,
    [ProductFlow.Caterer]: ChefHat,
    [ProductFlow.Kumo]: Soup,
};

/**
 * La même pastille que `BundleTypeTag`, repliée sur son icône — mêmes couleurs, le libellé passe en
 * infobulle. Pour les endroits où la place manque, comme l'en-tête d'une tuile de kanban.
 */
export const BundleTypeIcon = ({ type }: { type: ProductFlow }) => {
    const { backgroundColor, labelColor } = getColorsFromType(type);
    const Icon = FLOW_ICONS[type];
    return (
        <IconContainer $color={backgroundColor} title={BUNDLE_TYPES_LABELS[type]}>
            <Icon size={13} color={labelColor} />
        </IconContainer>
    );
};

export const BundleTypeTag = ({
    type,
    label = '',
    customMargin,
}: {
    type: ProductFlow | null;
    label?: string;
    customMargin?: string;
}) => {
    const { backgroundColor, labelColor, borderColor } = getColorsFromType(type);
    return (
        <TagContainer $color={backgroundColor} $borderColor={borderColor} $customMargin={customMargin}>
            <TagLabel $color={labelColor}>{type === null ? label : BUNDLE_TYPES_LABELS[type]}</TagLabel>
        </TagContainer>
    );
};

const TagContainer = styled.div<{ $color: string; $borderColor: string; $customMargin?: string }>`
    display: flex;
    align-items: center;
    justify-content: center;
    border-width: 1px;
    background-color: ${({ $color }: { $color: string }) => $color}dd;
    padding: 5px 15px;
    border-radius: 20px;
    max-width: 150px;
    text-align: center;
    ${({ $customMargin }) => $customMargin && `margin: ${$customMargin}`};
`;

const TagLabel = styled.div<{ $color: string }>`
    color: ${({ $color }) => $color};
    font-size: 14px;
`;

const IconContainer = styled.div<{ $color: string }>`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 6px;
    background-color: ${({ $color }: { $color: string }) => $color}dd;
`;
